import { ColumnDef, RowData, RowSelectionState } from '@tanstack/react-table'
import { useMemo } from 'react'

import { useRangeSelectionHandler } from './useRangeSelection'
import { Checkbox } from '@fp/ui-kit'

interface UseTableSelectionColumnsProps<T extends RowData> {
  enableRowSelection: boolean
  columns: ColumnDef<T>[]
  orderableFields?: string[]
  setRowSelection?: (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => void
}

export function useTableSelectionColumns<T extends RowData>({
  enableRowSelection,
  columns,
  orderableFields,
  setRowSelection,
}: UseTableSelectionColumnsProps<T>): ColumnDef<T>[] {
  const { handleRowClick, resetAnchor } = useRangeSelectionHandler<T>(setRowSelection)

  return useMemo(() => {
    if (!enableRowSelection) {
      if (orderableFields) {
        return columns.map((col) => {
          const colId = 'accessorKey' in col && col.accessorKey ? col.accessorKey : col.id

          return { ...col, enableSorting: orderableFields.includes(colId as string) }
        })
      }

      return columns
    }

    const selectionColumn: ColumnDef<T> = {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => {
            resetAnchor() // ← reset range state
            table.toggleAllPageRowsSelected(!!value)
          }}
          aria-label='Select all'
        />
      ),
      cell: ({ row, table }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={() => {}}
          onClick={(e) => {
            e.stopPropagation()
            handleRowClick(row.index, row.id, e.shiftKey, table.getRowModel().rows)
          }}
          aria-label='Select row'
        />
      ),
      enableSorting: false,
      enableHiding: false,
      maxSize: 50,
      meta: {
        isDynamic: false,
      },
    }

    const mappedColumns = orderableFields
      ? columns.map((col) => {
          const colId = 'accessorKey' in col && col.accessorKey ? col.accessorKey : col.id

          return { ...col, enableSorting: orderableFields.includes(colId as string) }
        })
      : columns

    return [selectionColumn, ...mappedColumns]
  }, [enableRowSelection, columns, orderableFields, handleRowClick, resetAnchor])
}
